import { useCallback, useEffect, useState } from 'react';
import { Button, Grid, Input, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import RegionsDropdown from '../../../components/RegionsDropdown';
import ZoneDropdown from '../../../components/ZonesDropdown';
import { useAuth } from '../../../hooks/useAuth';
import { UndoOutlined } from '@ant-design/icons';
import { ApplicationsFilter } from '../../../shared/VolunteerApplicationServiceTypes';
import { VALID_ABHYASI_ID_REGEX } from '../../../shared/Constants';
import { CentralTeamDashboardFilter } from '../../../shared/ScreeningServiceTypes';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface FilterProps {
	open: boolean;
	onClose: () => void;
	filterValue: ApplicationsFilter | undefined;
	onFilterChange: (value: ApplicationsFilter) => void;
	CTDFilter?: CentralTeamDashboardFilter;
	onResetFilters?: () => void;
	resetFromList?: boolean;
}

interface OptionData {
	value: number | undefined;
	label: string | undefined;
}

interface ZoneOption {
	value: string;
	label: string;
}

export const FilterCTDApplications = ({
	open,
	onClose,
	filterValue,
	onFilterChange,
	CTDFilter,
	onResetFilters,
	resetFromList,
}: FilterProps) => {
	const { t, i18n } = useTranslation();
	const lengthyLanguages = ['ta', 'ma', 'te', 'ka'];
	const isLengthyLanguage = lengthyLanguages.includes(i18n.language);
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const { srcmId } = useAuth();
	const [zoneNames, setZoneNames] = useState<Array<string> | undefined>();
	const [selectedZones, setSelectedZones] = useState<ZoneOption[]>([]);
	const [regions, setRegions] = useState<OptionData>();
	const [nameValue, setNameValue] = useState<string | undefined>(
		filterValue?.searchByApplicantName
	);
	const [applicantSrcmIdValue, setApplicantSrcmId] = useState<
		string | undefined
	>(filterValue?.applicantSrcmId);
	const [applicantSrcmIdError, setApplicantSrcmIdError] = useState('');

	const processFilterData = (
		filter: CentralTeamDashboardFilter | ApplicationsFilter
	) => {
		const selectedRegions =
			'regionId' in filter
				? filter.regionId
				: (filter as ApplicationsFilter).regionIds;
		setRegions(
			selectedRegions?.[0] !== undefined
				? { value: selectedRegions[0], label: undefined }
				: undefined
		);
	};

	useEffect(() => {
		if (CTDFilter) processFilterData(CTDFilter);
	}, [CTDFilter]);

	useEffect(() => {
		if (filterValue) processFilterData(filterValue);
	}, [filterValue]);

	useEffect(() => {
		if (!resetFromList) {
			setNameValue('');
			setZoneNames(undefined);
			setSelectedZones([]);
			setRegions(undefined);
			setApplicantSrcmId('');
			setApplicantSrcmIdError('');
		}
	}, [resetFromList]);

	const onApplicantNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setNameValue(e.target.value);
		},
		[]
	);

	const onApplicantSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setApplicantSrcmId(value);
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setApplicantSrcmIdError('invalidAbhyasiId');
			} else {
				setApplicantSrcmIdError('');
			}
		},
		[]
	);

	const handleZoneChange = useCallback(
		(value: Array<{ value: string; label: string }>) => {
			const arr = Array.isArray(value) ? value : value ? [value] : [];

			const zoneName = arr.map((z: { label: string }) => z.label);
			setZoneNames(zoneName);

			setSelectedZones(arr);
		},
		[]
	);

	const handleRegionsChange = useCallback((value: OptionData) => {
		setSelectedZones([]);
		setZoneNames(undefined);
		setRegions(value);
	}, []);

	const handleResetFilters = useCallback(() => {
		if (onResetFilters) {
			setNameValue('');
			setApplicantSrcmId('');
			setApplicantSrcmIdError('');
			setSelectedZones([]);
			setZoneNames(undefined);
			setRegions(undefined);
			onResetFilters();

			onClose();
		}
	}, [onClose, onResetFilters]);

	const handleFilterButtonClick = useCallback(() => {
		const regionIntValue = regions?.value;
		const updatedFilter = {
			...filterValue,
			applicantSrcmId: applicantSrcmIdValue,
			searchByApplicantName: nameValue,
			regionIds: regionIntValue ? [regionIntValue] : [],
			zoneNames,
		};
		onFilterChange(updatedFilter);
		onClose();
	}, [
		onClose,
		onFilterChange,
		regions?.value,
		applicantSrcmIdValue,
		nameValue,
		filterValue,
		zoneNames,
	]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			centered
			className={
				isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
			}
			closable={true}
			maskClosable={true}
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('filterDashboard')}
			</div>
			<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">
							{t('filterByApplicantName')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={nameValue}
							allowClear
							onChange={onApplicantNameChange}
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="text-xs text-gray-650">
							{t('filterByApplicantSRCMId')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={applicantSrcmIdValue}
							allowClear
							onChange={onApplicantSRCMIdChange}
						/>
						{applicantSrcmIdError && (
							<Text className="text-xs text-red-500">
								{t(applicantSrcmIdError)}
							</Text>
						)}
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByRegion')}
						</Text>
						<RegionsDropdown
							functionarySrcmId={srcmId}
							value={regions}
							onChange={handleRegionsChange}
							getRegionChange={true}
						/>
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('filterByZone')}
						</Text>
						<ZoneDropdown
							functionarySrcmId={srcmId}
							value={selectedZones}
							onChange={handleZoneChange}
							regionId={regions?.value}
							disabled={!regions?.value}
							key={regions?.value}
							multiple={true}
							getZoneChange={true}
						/>
					</div>
				</div>
			</div>
			<div className="flex justify-center">
				<Button
					className="block sm:hidden flex-1 h-9 text-primary-blue font-sans font-bold text-center"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}

						<UndoOutlined className="ml-2" />
					</span>
				</Button>
			</div>
			<div
				className={`flex ${
					isStackVertical
						? 'flex-col items-center space-y-2'
						: 'justify-center gap-2'
				} mt-4 ant-modal-footer`}
			>
				<Button
					className="hidden sm:block flex-1 h-9 text-primary-blue font-sans font-bold"
					type="link"
					onClick={handleResetFilters}
				>
					<span>
						{t('resetFilters')}
						<UndoOutlined className="ml-2" />
					</span>
				</Button>
				<Button
					className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? 'p-2' : ''
					}`}
					onClick={onClose}
				>
					{t('closeFilter')}
				</Button>
				<Button
					type="primary"
					onClick={handleFilterButtonClick}
					className={`flex-1 h-9 font-sans font-bold ${
						isStackVertical ? 'p-2 mt-3' : ''
					}`}
				>
					{t('applyFilter')}
				</Button>
			</div>
		</Modal>
	);
};
